import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { z } from 'zod';
import { ManagerAuditAction } from '@odonix/contracts';
import { useSession } from '../lib/session/context.js';
import { AppShell } from '../shell/app-shell.js';
import { NotFoundScreen } from '../shell/not-found-screen.js';
import { PeriodSearch } from '../ui/period.js';
import { HomeScreen } from '../features/home/home-screen.js';
import { TenantsListScreen } from '../features/tenants/tenants-list-screen.js';
import { TenantDetailScreen } from '../features/tenants/tenant-detail-screen.js';
import { AuditLogScreen } from '../features/audit/audit-log-screen.js';
import { ManagersScreen } from '../features/managers/managers-screen.js';
import { ManagerScreen } from '../features/managers/manager-screen.js';
import { ManagerAuditScreen } from '../features/managers/manager-audit-screen.js';
import { ExpensesScreen } from '../features/expenses/expenses-screen.js';

/**
 * Маршруты рабочей части.
 *
 * Контекста роутера здесь нет: `staff` и API клиента читаются обычным
 * React-контекстом (`useSession`, `useTenantsApi`) — оба уже стоят выше
 * `RouterProvider` в дереве.
 *
 * Отборы и периоды живут в адресной строке (`validateSearch`), а не в
 * локальном состоянии экрана: «каждая цифра ведёт к первичному документу»
 * требует, чтобы обзор мог сослаться на «активных клиник» или «расходы
 * октября» ссылкой, а не описанием того, что нужно нажать самому.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: function Shell() {
    const session = useSession();
    if (session.status !== 'ready') return null;

    return (
      <AppShell staff={session.staff}>
        <Outlet />
      </AppShell>
    );
  },
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): PeriodSearch => PeriodSearch.parse(search),
  component: HomeScreen,
});

const TenantsSearch = z.object({
  q: z.string().optional().catch(undefined),
  state: z.enum(['trial', 'active', 'grace', 'restricted', 'suspended']).optional().catch(undefined),
  kind: z.enum(['clinic', 'lab']).optional().catch(undefined),
  managerId: z.union([z.uuid(), z.literal('none')]).optional().catch(undefined),
});
export type TenantsSearch = z.infer<typeof TenantsSearch>;

const tenantsListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenants',
  validateSearch: (search: Record<string, unknown>): TenantsSearch => TenantsSearch.parse(search),
  component: TenantsListScreen,
});

const tenantDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenants/$tenantId',
  component: function TenantDetail() {
    const { tenantId } = tenantDetailRoute.useParams();
    return <TenantDetailScreen key={tenantId} tenantId={tenantId} />;
  },
});

const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/audit',
  component: AuditLogScreen,
});

const ManagersSearch = PeriodSearch.extend({
  show: z.enum(['active', 'disabled']).optional().catch(undefined),
});
export type ManagersSearch = z.infer<typeof ManagersSearch>;

const managersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/managers',
  validateSearch: (search: Record<string, unknown>): ManagersSearch => ManagersSearch.parse(search),
  component: ManagersScreen,
});

const ManagerSearch = PeriodSearch.extend({
  tab: z.enum(['overview', 'tenants', 'earnings', 'links', 'actions']).optional().catch(undefined),
});
export type ManagerSearch = z.infer<typeof ManagerSearch>;

const managerRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/managers/$managerId',
  validateSearch: (search: Record<string, unknown>): ManagerSearch => ManagerSearch.parse(search),
  component: function Manager() {
    const { managerId } = managerRoute.useParams();
    return <ManagerScreen key={managerId} managerId={managerId} />;
  },
});

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const ManagerAuditSearch = z.object({
  managerId: z.uuid().optional().catch(undefined),
  action: ManagerAuditAction.optional().catch(undefined),
  from: Day.optional().catch(undefined),
  to: Day.optional().catch(undefined),
});
export type ManagerAuditSearch = z.infer<typeof ManagerAuditSearch>;

const managerAuditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/managers/audit',
  validateSearch: (search: Record<string, unknown>): ManagerAuditSearch => ManagerAuditSearch.parse(search),
  component: ManagerAuditScreen,
});

const ExpensesSearch = PeriodSearch.extend({
  categoryId: z.uuid().optional().catch(undefined),
  q: z.string().max(200).optional().catch(undefined),
});
export type ExpensesSearch = z.infer<typeof ExpensesSearch>;

const expensesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/expenses',
  validateSearch: (search: Record<string, unknown>): ExpensesSearch => ExpensesSearch.parse(search),
  component: ExpensesScreen,
});

const routeTree = rootRoute.addChildren([
  shellRoute.addChildren([
    homeRoute,
    tenantsListRoute,
    tenantDetailRoute,
    auditRoute,
    managersRoute,
    managerAuditRoute,
    managerRoute,
    expensesRoute,
  ]),
]);

export function createAppRouter() {
  // Свой экран на неизвестный адрес: по умолчанию роутер печатает `Not Found`
  // без оболочки и без выхода.
  return createRouter({ routeTree, defaultNotFoundComponent: NotFoundScreen });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
